import { useEffect } from "react";
import { useGetChat } from "~/api/chat/chat";
import { useGetCustomReport } from "~/api/custom-reports/custom-reports";
import { useReportStore } from "../../stores/reportStore";
import { useTermsStore } from "../../stores/termsStore";
import { useTemplateReports } from "./useTemplateReports";

export type { TemplateReport } from "./useTemplateReports";

export const useReportData = (
  organizationId: string,
  projectId: string,
  chatId: string
) => {
  const selectedReportIndex = useReportStore((s) =>
    s.getSelectedReportIndex(chatId)
  );
  const setSelectedReportIndexInStore = useReportStore(
    (s) => s.setSelectedReportIndex
  );
  const setSelectedReportIndex = (index: number) =>
    setSelectedReportIndexInStore(chatId, index);

  const {
    templateReports,
    locallyGenerating,
    handleGenerateReport,
    isTemplatesLoading,
    isReportsLoading,
  } = useTemplateReports(organizationId, projectId);

  const { data: chat } = useGetChat(organizationId, projectId, chatId, {
    query: { staleTime: Infinity },
  });

  const selectedReport =
    templateReports.length > selectedReportIndex
      ? templateReports[selectedReportIndex].report
      : null;

  const reportId = selectedReport?.id ?? "";

  const { data: reportData, isLoading: isReportContentLoading } =
    useGetCustomReport(organizationId, projectId, reportId, {
      query: {
        enabled: !!reportId,
        refetchInterval: (query) => {
          if (!query.state.data) {
            return false;
          }

          const sections = query.state.data.reportSections ?? [];
          const needsPolling = sections.some(
            (section) =>
              section.status !== "completed" && section.status !== "failed"
          );

          return needsPolling ? 3000 : false;
        },
        refetchIntervalInBackground: true,
      },
    });

  const addTerms = useTermsStore((s) => s.addTerms);
  useEffect(() => {
    if (!reportData) return;

    const hint = reportData.hint;
    if (!hint || hint.length === 0) return;

    addTerms(projectId, hint);
  }, [reportData, projectId, addTerms]);

  const content = reportData?.text ?? "";

  return {
    content,
    locallyGenerating,
    handleGenerateReport,
    templateReports,
    isLoading:
      isTemplatesLoading ||
      isReportsLoading ||
      (!!reportId && isReportContentLoading),
    selectedReportIndex,
    setSelectedReportIndex,
    chatTitle: chat?.title ?? "",
    reportId,
    reportSections: reportData?.reportSections,
    isSourcesAddedToRaw: reportData?.isSourcesAddedToRaw ?? false,
  };
};
